// Geolocalización + reverse geocoding (Nominatim, OpenStreetMap).
// Formato de geoInfo: "lat,lng;acc=…;origen=…" — ver src/lib/geoInfo.js.

import { parseGeoInfo as parseFullGeoInfo } from './geoInfo';

export class GeoPermissionError extends Error {
  constructor(message = 'Permisos de geolocalización denegados.') {
    super(message);
    this.name = 'GeoPermissionError';
    this.reason = 'permission';
  }
}

// `getCurrentPosition` devuelve la PRIMERA lectura disponible, que en
// escritorio sin GPS suele ser la estimación basada en IP del ISP — y la IP
// puede estar registrada en otra ciudad (Madrid es muy típico para ISPs ES).
//
// Google Maps usa `watchPosition` para ir mejorando la lectura: empieza con
// IP/Wi-Fi cercano y, mientras el navegador escanea más, va devolviendo
// lecturas con menor `accuracy`. Aquí replicamos esa estrategia: aceptamos
// la lectura cuando su precisión es suficiente o, en su defecto, la mejor
// vista al cumplirse el timeout.
const watchAndPickBest = (opts) => new Promise((resolve, reject) => {
  let settled = false;
  let watchId = null;
  let best = null; // { coords:{latitude,longitude,accuracy} }

  const cleanUp = () => {
    if (watchId != null && navigator.geolocation?.clearWatch) {
      try { navigator.geolocation.clearWatch(watchId); } catch {}
    }
  };

  const finishWithBest = () => {
    if (settled) return;
    settled = true;
    cleanUp();
    if (best) resolve(best);
    else {
      const e = new Error('timeout');
      e.code = 3;
      reject(e);
    }
  };

  const accuracyTarget = Number.isFinite(opts.accuracyTarget) ? opts.accuracyTarget : 100;
  const totalTimeout = (opts.timeout || 14000) + 1000;
  const timer = setTimeout(finishWithBest, totalTimeout);

  watchId = navigator.geolocation.watchPosition(
    (pos) => {
      if (settled) return;
      const acc = pos?.coords?.accuracy ?? Infinity;
      if (!best || acc < (best.coords?.accuracy ?? Infinity)) best = pos;
      // Si esta lectura ya es lo bastante precisa, cerramos.
      if (acc <= accuracyTarget) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        cleanUp();
        resolve(pos);
      }
    },
    (err) => {
      if (settled) return;
      // Si tenemos al menos una lectura buena, no abortamos por error
      // intermedio; solo reaccionamos si todavía no hay nada.
      if (best) return;
      settled = true;
      clearTimeout(timer);
      cleanUp();
      reject(err);
    },
    opts
  );
});

// Consulta el estado real del permiso de geolocalización vía Permissions API
// (no todos los navegadores la implementan; si no, devolvemos null).
const queryGeoPermissionState = async () => {
  try {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return null;
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status?.state || null;
  } catch {
    return null;
  }
};

export const requestGeolocation = async () => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    throw new GeoPermissionError('Este navegador no soporta geolocalización.');
  }
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    throw new GeoPermissionError(
      'La geolocalización requiere una conexión segura (HTTPS). Si la web va por HTTP, abre la versión https://… y reintenta.'
    );
  }

  const permState = await queryGeoPermissionState();
  if (permState === 'denied') {
    throw new GeoPermissionError(
      'No tenemos permiso para acceder a tu ubicación. Activa la ubicación para esta web desde los ajustes del navegador y recarga la página.'
    );
  }

  // Intento 1: alta precisión + watch hasta accuracy razonable.
  // El string devuelto incluye la accuracy declarada por el navegador (en metros)
  // tras el sufijo `;acc=`. Los consumidores ignoran ese sufijo (lo parsea
  // parseGeoInfo); sirve sólo para diagnóstico cuando el geofence rechaza.
  try {
    const pos = await watchAndPickBest({ timeout: 14000, enableHighAccuracy: true, maximumAge: 0, accuracyTarget: 100 });
    const acc = Math.round(pos?.coords?.accuracy ?? 0);
    return `${pos.coords.latitude},${pos.coords.longitude};acc=${acc}`;
  } catch (err1) {
    const code1 = err1?.code ?? null;

    if (code1 === 2 || code1 === 3) {
      try {
        const pos = await watchAndPickBest({ timeout: 14000, enableHighAccuracy: false, maximumAge: 0, accuracyTarget: 1000 });
        const acc = Math.round(pos?.coords?.accuracy ?? 0);
        return `${pos.coords.latitude},${pos.coords.longitude};acc=${acc}`;
      } catch (err2) {
        const code2 = err2?.code ?? null;
        if (code2 === 1) {
          throw new GeoPermissionError('Necesitamos acceso a tu ubicación para poder fichar. Activa la ubicación en el navegador y recarga la página.');
        }
        if (code2 === 2) {
          throw new GeoPermissionError('No se pudo determinar tu ubicación. Comprueba que el GPS o la conexión de red estén activos.');
        }
        if (code2 === 3) {
          throw new GeoPermissionError('La ubicación tardó demasiado en responder. Inténtalo de nuevo en unos segundos.');
        }
        throw new GeoPermissionError('No se pudo obtener la ubicación.');
      }
    }

    if (code1 === 1) {
      throw new GeoPermissionError('Necesitamos acceso a tu ubicación para poder fichar. Activa la ubicación en el navegador y recarga la página.');
    }
    throw new GeoPermissionError('No se pudo obtener la ubicación.');
  }
};

// Solo las coordenadas ({ lat, lng } | null). El formato completo (origen,
// precisión, aprobador…) lo lee src/lib/geoInfo.js, compartido con la app.
export const parseGeoInfo = (geoInfo) => {
  if (!geoInfo || typeof geoInfo !== 'string') return null;
  const p = parseFullGeoInfo(geoInfo);
  return p.hasCoords ? { lat: p.lat, lng: p.lng } : null;
};

// Cache en memoria (session-scope). Nominatim pide no hamaquear.
const labelCache = new Map();
const pending = new Map();

const formatLabel = (data) => {
  if (!data) return null;
  const a = data.address || {};
  // Preferimos sitio específico → calle + ciudad/municipio
  const site = a.shop || a.office || a.building || a.amenity || null;
  const road = [a.road, a.house_number].filter(Boolean).join(' ');
  const city = a.city || a.town || a.village || a.municipality || a.hamlet || a.county || '';
  const parts = [site, road || null, city || null].filter(Boolean);
  if (parts.length > 0) return parts.join(', ');
  return data.display_name || null;
};

export const reverseGeocode = async (geoInfo) => {
  const coords = parseGeoInfo(geoInfo);
  if (!coords) return null;
  const key = `${coords.lat.toFixed(5)},${coords.lng.toFixed(5)}`;
  if (labelCache.has(key)) return labelCache.get(key);
  if (pending.has(key)) return pending.get(key);

  const promise = (async () => {
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1&accept-language=es`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const label = formatLabel(data);
      labelCache.set(key, label);
      return label;
    } catch {
      labelCache.set(key, null);
      return null;
    } finally {
      pending.delete(key);
    }
  })();
  pending.set(key, promise);
  return promise;
};

// Forward geocoding: dirección libre → "lat,lng" (string) + dirección normalizada.
// Devuelve { coords: 'lat,lng' | null, label, raw } o null si no encuentra.
export const forwardGeocode = async (address) => {
  const q = (address || '').trim();
  if (!q) return null;
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&countrycodes=es&addressdetails=1&limit=5&accept-language=es`;
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const arr = Array.isArray(data) ? data : [];
    if (arr.length === 0) return null;
    return arr.map((item) => ({
      coords: `${item.lat},${item.lon}`,
      label: formatLabel(item) || item.display_name,
      raw: item,
    }));
  } catch {
    return null;
  }
};

// Distancia en metros entre dos puntos (Haversine). lat/lng en grados.
export const distanceMeters = (lat1, lng1, lat2, lng2) => {
  if (![lat1, lng1, lat2, lng2].every((n) => Number.isFinite(n))) return null;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Distancia en metros entre dos strings "lat,lng" (formato del backend).
export const distanceBetweenGeoInfo = (a, b) => {
  const A = parseGeoInfo(a);
  const B = parseGeoInfo(b);
  if (!A || !B) return null;
  return distanceMeters(A.lat, A.lng, B.lat, B.lng);
};
